/**
 * Background text extraction for uploaded files. The extract-file function
 * reads the file and caches the text on the item (content.extracted_text), so
 * the file is searchable and Partnr can answer from it.
 */

import { supabase } from '@/integrations/supabase/client';
import { useCanvasStore } from '@/stores/canvasStore';

// Keep in sync with classifyFile in supabase/functions/_shared/fileReader.ts
const EXTRACTABLE_EXTENSIONS = /\.(pdf|txt|md|markdown|csv|tsv|json|xml|ya?ml|log|html?|docx|pptx|xlsx)$/i;
const MAX_EXTRACT_BYTES = 20 * 1024 * 1024;

export function isExtractableFile(filename: string, mimeType: string, size?: number): boolean {
  if (size !== undefined && size > MAX_EXTRACT_BYTES) return false;
  return mimeType === 'application/pdf' || mimeType.startsWith('text/') || EXTRACTABLE_EXTENSIONS.test(filename);
}

/** Fire-and-forget. Merges the result into the local store so later edits keep it. */
export function requestFileExtraction(itemId: string): void {
  supabase.functions
    .invoke('extract-file', { body: { itemId } })
    .then(({ data, error }) => {
      if (error || typeof data?.extracted_text !== 'string') return;
      const merge = <T extends { id: string; content: unknown }>(item: T): T =>
        item.id === itemId
          ? {
              ...item,
              content: {
                ...(item.content as Record<string, unknown>),
                extracted_text: data.extracted_text,
                extracted_at: new Date().toISOString(),
                extraction_truncated: !!data.extraction_truncated,
              },
            }
          : item;
      useCanvasStore.setState((state) => ({
        items: state.items.map(merge),
        allItems: state.allItems.map(merge),
      }));
    })
    .catch((e) => console.warn('[FileExtraction] failed:', e));
}
